import React, { useRef } from "react";
import styles from "./CoolSwitch.module.css";

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * @typedef {Object} CoolSwitchCustomProps
 * @property {boolean | number} [checked] - The checked state of the switch. Can be boolean or number.
 * @property {(e: import('react').ChangeEvent<HTMLInputElement>) => void} [onChange] - Callback triggered when the switch state changes.
 * @property {(e: import('react').ChangeEvent<HTMLInputElement>) => void} [switchChange] - Additional callback triggered when the switch state changes.
 * @property {boolean} [disabled=false] - Whether the switch is disabled.
 * @property {import('react').ReactNode} [label] - Label to display next to the switch.
 * @property {string} [color] - Theme color for the checked state (hex string).
 * @property {string | number | boolean} [value] - Value of the switch.
 * @property {import('react').ReactNode} [checkedLabel] - Label to display when the switch is checked.
 * @property {import('react').ReactNode} [uncheckedLabel] - Label to display when the switch is unchecked.
 * 
 * @typedef {import('react').InputHTMLAttributes<HTMLInputElement> & CoolSwitchCustomProps} CoolSwitchProps
 */

/**
 * A customized switch component that supports labels, custom colors, and detailed state presentation.
 * 
 * @param {CoolSwitchProps} props
 * @returns {import('react').ReactElement}
 */
const CoolSwitch = ({
  checked,
  onChange,
  switchChange,
  disabled = false,
  label = undefined,
  color = undefined,
  value,
  checkedLabel = undefined,
  uncheckedLabel = undefined,
  className = '',
  ...rest
}) => {
  const inputRef = useRef(null);

  const handleToggle = (e) => {
    if (e.target === inputRef.current) return;
    e.preventDefault();
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleChange = (e) => {
    onChange?.(e);
    switchChange?.(e);
  };

  const checkedColor = (color && color !== 'default') ? color : "#4b1c46";
  const uncheckedColor = "#D8D8D8";

  const trackStyle = {
    backgroundColor: checked
      ? hexToRgba(checkedColor, 0.5)
      : "rgba(213, 210, 213, 0.6)",
  };
  const thumbStyle = {
    backgroundColor: checked ? checkedColor : uncheckedColor,
  };
  const statusStyle = checked ? { color: checkedColor } : undefined;

  return (
    <label
      className={[styles.wrapper, disabled && styles.disabled, className]
        .filter(Boolean)
        .join(" ")}
    >
      {label && <span className={styles.switchLabel}>{label}</span>}
      <span className={styles.switchRoot} onClick={handleToggle}>
        <input
          ref={inputRef}
          type="checkbox"
          className={styles.hiddenInput}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          {...rest}
        />
        <div
          className={[styles.track, checked && styles.checked]
            .filter(Boolean)
            .join(" ")}
          style={trackStyle}
        >
          <div
            className={[styles.thumb, checked && styles.checked]
              .filter(Boolean)
              .join(" ")}
            style={thumbStyle}
          />
        </div>
      </span>
      {(checkedLabel || uncheckedLabel) && (
        <span
          className={[styles.statusLabel, checked && styles.checked]
            .filter(Boolean)
            .join(" ")}
          style={statusStyle}
        >
          {checked ? checkedLabel : uncheckedLabel}
        </span>
      )}
    </label>
  );
};

export default CoolSwitch;
